import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  SportsFootball as FootballIcon,
  Grid4x4 as GridIcon,
  EmojiEvents as WinnerIcon,
  Calculate as CalcIcon,
  Numbers as NumbersIcon,
  Timer as QuarterIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample, MiniGrid, Formula } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const GameMechanicsPage: React.FC = () => {
  useDocumentTitle('Game Mechanics - Support');

  return (
    <SupportLayout
      title="Game Mechanics"
      description="Understand how sports squares pools work, how winning squares are determined, and the math behind the game."
      breadcrumbs={[{ label: 'Game Mechanics' }]}
    >
      {/* The Grid */}
      <Box id="the-grid" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <GridIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          The 10x10 Grid
        </Typography>

        <Typography variant="body1" paragraph>
          A standard squares board is a <strong>10x10 grid</strong> creating 100 squares. One team's numbers run along the top (columns), and the other team's numbers run along the side (rows).
        </Typography>

        <VisualExample title="The Basic Grid Structure" caption="Standard board: 10 columns x 10 rows">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <MiniGrid
              rows={5}
              cols={5}
              homeNumbers={[0, 1, 2, 3, 4]}
              awayNumbers={[0, 1, 2, 3, 4]}
              homeTeam="Home Team"
              awayTeam="Away Team"
            />
          </Box>
        </VisualExample>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary.main">Columns (Home Team)</Typography>
              <Typography variant="body2">
                The <strong>horizontal numbers</strong> across the top represent the home team's last digit of the score. There are 10 columns, one for each digit (0-9).
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'error.main', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="error.main">Rows (Away Team)</Typography>
              <Typography variant="body2">
                The <strong>vertical numbers</strong> down the side represent the away team's last digit of the score. There are 10 rows, one for each digit (0-9).
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <InfoCard title="Home on Top vs Bottom" variant="info" icon={<GridIcon />}>
            <Typography variant="body2">
              By default, the home team is displayed on top (columns) and away team on the side (rows). Board managers can switch this orientation if preferred. The mechanics work the same either way.
            </Typography>
          </InfoCard>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Winning Squares */}
      <Box id="winning-squares" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <WinnerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          How Winning Squares Are Determined
        </Typography>

        <Typography variant="body1" paragraph>
          The winning square is determined by the <strong>last digit</strong> of each team's score. The intersection of those two digits on the grid is the winning square.
        </Typography>

        {/* Randomized numbers: KC columns [3,7,1,9,0,5,2,8,6,4], PHI rows [8,2,5,0,9,4,1,3,6,7]
             To find winner with KC=17 (digit 7) and PHI=14 (digit 4):
             - Column with 7: index 1 (second column)
             - Row with 4: index 5 (sixth row)
             So highlighted should be row: 5, col: 1 */}
        <VisualExample title="Finding the Winner" caption="Score: Chiefs 17, Eagles 14">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Step by Step:</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><NumbersIcon color="primary" /></ListItemIcon>
                    <ListItemText
                      primary="Chiefs score: 17"
                      secondary="Last digit = 7 → find 7 in columns"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><NumbersIcon color="error" /></ListItemIcon>
                    <ListItemText
                      primary="Eagles score: 14"
                      secondary="Last digit = 4 → find 4 in rows"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><WinnerIcon color="success" /></ListItemIcon>
                    <ListItemText
                      primary="Winner!"
                      secondary="Square where 4 and 7 meet"
                      primaryTypographyProps={{ fontWeight: 'bold', color: 'success.main' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MiniGrid
                  rows={10}
                  cols={10}
                  homeNumbers={[3, 7, 1, 9, 0, 5, 2, 8, 6, 4]}
                  awayNumbers={[8, 2, 5, 0, 9, 4, 1, 3, 6, 7]}
                  highlighted={[{ row: 5, col: 1, color: '#4caf50', label: 'WIN' }]}
                  homeTeam="KC"
                  awayTeam="PHI"
                />
              </Box>
            </Grid>
          </Grid>
        </VisualExample>

        <Formula
          formula="Winner = grid[away_score % 10][home_score % 10]"
          variables={[
            { name: 'away_score % 10', value: '14 % 10 = 4', description: 'Eagles last digit (row)' },
            { name: 'home_score % 10', value: '17 % 10 = 7', description: 'Chiefs last digit (column)' },
          ]}
        />

        <Alert severity="success" sx={{ mt: 2 }}>
          <strong>That's it!</strong> The math is simple: take the last digit of each score, find where they intersect. Whoever owns that square wins the payout!
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Number Randomization */}
      <Box id="number-randomization" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <NumbersIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Number Randomization
        </Typography>

        <Typography variant="body1" paragraph>
          Before the game, the digits 0-9 are <strong>randomly assigned</strong> to each column and row. This is what makes the game fair—you don't get to pick your numbers!
        </Typography>

        <VisualExample title="Before vs After Randomization" caption="Numbers are shuffled independently for each team">
          <Grid container spacing={4} justifyContent="center">
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }}>Sequential (Before)</Typography>
              <MiniGrid
                rows={5}
                cols={5}
                homeNumbers={[0, 1, 2, 3, 4]}
                awayNumbers={[0, 1, 2, 3, 4]}
                homeTeam="Home"
                awayTeam="Away"
              />
            </Grid>
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }}>Randomized (After)</Typography>
              <MiniGrid
                rows={5}
                cols={5}
                homeNumbers={[7, 2, 9, 0, 5]}
                awayNumbers={[3, 8, 1, 6, 4]}
                homeTeam="Home"
                awayTeam="Away"
              />
            </Grid>
          </Grid>
        </VisualExample>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Why Randomize?</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Ensures fairness" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Prevents strategic square selection" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Adds excitement before reveal" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>When to Randomize</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="After all squares are claimed" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Before locking the board" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Usually right before game time" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <InfoCard title="Numbers Can Be Hidden" variant="tip">
          <Typography variant="body2">
            After randomization, you can choose to keep numbers hidden until you're ready. Many organizers reveal numbers at kickoff for maximum suspense!
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Multi-Number Sets */}
      <Box id="multi-number-sets" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <NumbersIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Multi-Number Sets (Half/Quarter Mode)
        </Typography>

        <Typography variant="body1" paragraph>
          By default, boards use a <strong>single set of numbers</strong> for the entire game. However, you can configure boards with <strong>different number sets for different periods</strong>, adding an extra layer of excitement!
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Single Mode (Default)</Typography>
              <Chip label="1 Number Set" size="small" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                One set of numbers for the entire game. Simple and traditional—the same numbers apply to all quarters.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary.main">Half Mode</Typography>
              <Chip label="2 Number Sets" size="small" color="primary" sx={{ mb: 1 }} />
              <Typography variant="body2">
                Two different sets: <strong>1H</strong> for the first half (Q1, Q2) and <strong>2H</strong> for the second half (Q3, Q4).
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'secondary.main', bgcolor: 'secondary.50', height: '100%' }}>
              <Typography variant="h6" gutterBottom color="secondary.main">Quarter Mode</Typography>
              <Chip label="4 Number Sets" size="small" color="secondary" sx={{ mb: 1 }} />
              <Typography variant="body2">
                Each quarter has its own numbers: <strong>Q1</strong>, <strong>Q2</strong>, <strong>Q3</strong>, and <strong>Q4</strong> are all unique.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <VisualExample title="Half Mode vs Single Mode Example" caption="Half mode shows two rows of numbers at the top and two columns on the left">
          <Grid container spacing={4} justifyContent="center">
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }}>Single Mode</Typography>
              <MiniGrid
                rows={5}
                cols={5}
                homeNumbers={[3, 7, 0, 5, 2]}
                awayNumbers={[8, 1, 4, 9, 6]}
                homeTeam="Home"
                awayTeam="Away"
              />
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }} color="text.secondary">
                Same numbers all game
              </Typography>
            </Grid>
            <Grid size="grow">
              <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }} color="primary.main">Half Mode</Typography>
              <Box sx={{ position: 'relative' }}>
                <MiniGrid
                  rows={5}
                  cols={5}
                  homeNumbers={[3, 7, 0, 5, 2]}
                  awayNumbers={[8, 1, 4, 9, 6]}
                  homeTeam="Home"
                  awayTeam="Away"
                />
              </Box>
              <Typography variant="body2" textAlign="center" sx={{ mt: 1 }} color="text.secondary">
                1H: 3,7,0,5,2 | 2H: 9,2,6,1,8
              </Typography>
            </Grid>
          </Grid>
        </VisualExample>

        <InfoCard title="How Multi-Number Mode Works" variant="info" icon={<NumbersIcon />}>
          <Typography variant="body2" paragraph>
            When a board uses half or quarter mode:
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Multiple rows of numbers are shown at the top of the grid (one per number set)" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Multiple columns of numbers are shown on the left (one per number set)" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="When viewing a recorded score, the relevant number row/column is highlighted" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="All number sets are randomized together when the board is locked" />
            </ListItem>
          </List>
        </InfoCard>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Multi-Number Mode by Sport</Typography>
          <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sport</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Half Mode Keys</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quarter Mode Keys</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Football (4 quarters)</TableCell>
                  <TableCell>1H (Q1, Q2), 2H (Q3, Q4)</TableCell>
                  <TableCell>Q1, Q2, Q3, Q4</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Basketball (4 quarters)</TableCell>
                  <TableCell>1H (Q1, Q2), 2H (Q3, Q4)</TableCell>
                  <TableCell>Q1, Q2, Q3, Q4</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Hockey (3 periods)</TableCell>
                  <TableCell>1H (P1), 2H (P2, P3)</TableCell>
                  <TableCell>P1, P2, P3</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Soccer (2 halves)</TableCell>
                  <TableCell>1H, 2H</TableCell>
                  <TableCell>1H, 2H (same as half)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Paper>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>Tip:</strong> Multi-number mode is great for adding variety, especially for big games where you want more excitement. The number mode is set when creating a board and cannot be changed after publication.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Quarters and Scoring */}
      <Box id="quarters-scoring" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <QuarterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Quarters and Score Recording
        </Typography>

        <Typography variant="body1" paragraph>
          Most pools pay out multiple times during a game—typically after each quarter (or period). The score at the end of each quarter determines that quarter's winner.
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Quarter</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>When to Record</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Common Names</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Q1" size="small" /></TableCell>
                <TableCell>End of 1st quarter</TableCell>
                <TableCell>First Quarter</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Q2" size="small" color="primary" /></TableCell>
                <TableCell>End of 2nd quarter (halftime)</TableCell>
                <TableCell>Halftime, Half</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Q3" size="small" /></TableCell>
                <TableCell>End of 3rd quarter</TableCell>
                <TableCell>Third Quarter</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Q4" size="small" color="success" /></TableCell>
                <TableCell>End of game (or regulation)</TableCell>
                <TableCell>Final, Game</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <VisualExample title="Recording Scores Throughout the Game" caption="Enter scores after each quarter as the game progresses">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { quarter: 'Q1', home: 7, away: 3, winner: 'Row 3, Col 7', payout: '$100' },
              { quarter: 'Q2', home: 14, away: 10, winner: 'Row 0, Col 4', payout: '$200' },
              { quarter: 'Q3', home: 21, away: 17, winner: 'Row 7, Col 1', payout: '$100' },
              { quarter: 'Q4', home: 31, away: 24, winner: 'Row 4, Col 1', payout: '$600' },
            ].map((q) => (
              <Paper key={q.quarter} sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Chip label={q.quarter} color={q.quarter === 'Q4' ? 'success' : 'default'} />
                <Typography variant="body2">
                  Chiefs <strong>{q.home}</strong>, Eagles <strong>{q.away}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  → Last digits: {q.away % 10} & {q.home % 10}
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  {q.winner} wins {q.payout}
                </Typography>
              </Paper>
            ))}
          </Box>
        </VisualExample>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Different Sports:</strong> You can configure boards for 2, 3, or 4 scoring periods. Hockey might use 3 periods, while basketball uses 4 quarters. Adjust based on your sport!
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Score Probabilities */}
      <Box id="probabilities" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <CalcIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Score Digit Probabilities
        </Typography>

        <Typography variant="body1" paragraph>
          Not all number combinations are equally likely! In football, certain last digits appear more frequently due to scoring patterns (touchdowns = 6/7 points, field goals = 3 points).
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Typography variant="h6" gutterBottom>Football Scoring Facts</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">Touchdown + PAT</Typography>
              <Typography variant="h6">7 points</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">Field Goal</Typography>
              <Typography variant="h6">3 points</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">Touchdown + 2PT</Typography>
              <Typography variant="h6">8 points</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">Safety</Typography>
              <Typography variant="h6">2 points</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
          Based on historical data, here's how often each digit appears as a last digit in football:
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Digit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>0</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>1</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>2</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>3</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>4</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>5</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>6</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>7</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>8</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '9%' }}>9</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Frequency</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 'bold' }}>High</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 'bold' }}>High</TableCell>
                <TableCell align="center">Low</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 'bold' }}>High</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 'bold' }}>High</TableCell>
                <TableCell align="center">Low</TableCell>
                <TableCell align="center">Med</TableCell>
                <TableCell align="center" sx={{ bgcolor: 'success.100', fontWeight: 'bold' }}>Highest</TableCell>
                <TableCell align="center">Med</TableCell>
                <TableCell align="center">Low</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <InfoCard title="It's Still Random!" variant="info">
          <Typography variant="body2">
            While some digits are statistically more likely, the randomization process means any square can win. That's the fun of it—a "bad" number combination can still hit!
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Multiple Winners Same Square */}
      <Box id="same-winner" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <WinnerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Same Square Winning Multiple Times
        </Typography>

        <Typography variant="body1" paragraph>
          It's possible for the same square to win multiple quarters! This happens when the score's last digits are the same at different points in the game.
        </Typography>

        <VisualExample title="Same Winner Example" caption="Square at Row 7, Col 0 wins twice!">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">Q1 Score</Typography>
                <Typography variant="h6">Chiefs 10, Eagles 7</Typography>
                <Typography variant="body2">
                  Winner: Row <strong>7</strong>, Col <strong>0</strong>
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                <Typography variant="subtitle2" color="success.main">Q3 Score</Typography>
                <Typography variant="h6">Chiefs 20, Eagles 17</Typography>
                <Typography variant="body2">
                  Winner: Row <strong>7</strong>, Col <strong>0</strong> (same!)
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </VisualExample>

        <Alert severity="success" sx={{ mt: 2 }}>
          Lucky players can win multiple times on the same square! Each quarter is a separate payout, so winning the same square twice means double the earnings.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Supported Sports */}
      <Box id="supported-sports" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <FootballIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Supported Sports
        </Typography>

        <Typography variant="body1" paragraph>
          While sports squares pools are most popular for football, they work with any sport that has a numerical score!
        </Typography>

        <Grid container spacing={2}>
          {[
            { sport: 'Football', quarters: 4, notes: 'Traditional squares pool sport' },
            { sport: 'Basketball', quarters: 4, notes: 'Higher scores mean more variation' },
            { sport: 'Hockey', quarters: 3, notes: 'Three periods instead of quarters' },
            { sport: 'Soccer', quarters: 2, notes: 'Two halves, lower scores' },
            { sport: 'Golf', quarters: 4, notes: 'Use for tournament rounds' },
          ].map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.sport}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold">{item.sport}</Typography>
                <Chip label={`${item.quarters} periods`} size="small" sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">{item.notes}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default GameMechanicsPage;
